/**
 * Provider-specific streaming implementations.
 *
 * Each function lazily imports its SDK to avoid bundling unused providers.
 * All return AsyncGenerator<StreamChunk> for uniform consumption.
 */

import type { StreamChunk, ProviderConfig, Provider } from "../types";
import { PROVIDER_BASE_URLS } from "../types";
import { getModelsForProvider } from "../models";

/** Get the default model ID for a provider (first registered model). */
function defaultModelId(provider: Provider): string {
  const models = getModelsForProvider(provider);
  if (models.length === 0) {
    throw new Error(`No models registered for provider: ${provider}`);
  }
  return models[0].id;
}

/** Options passed to provider stream functions. */
export interface ProviderStreamOptions {
  config: ProviderConfig;
  systemPrompt: string;
  userPrompt: string;
  maxTokens: number;
  temperature: number;
  tier: "pro" | "flash";
  /** When provided, use this model ID directly instead of tier-based lookup. */
  modelId?: string;
  /** When set, request structured JSON output from the provider. */
  responseSchema?: Record<string, unknown>;
}

/** Stream from Google Gemini via @google/genai. */
export async function* streamGemini(
  opts: ProviderStreamOptions,
): AsyncGenerator<StreamChunk, void, unknown> {
  const { GoogleGenAI } = await import("@google/genai");
  const client = new GoogleGenAI({ apiKey: opts.config.apiKey });
  const model = opts.modelId ?? defaultModelId("gemini");

  const response = await client.models.generateContentStream({
    model,
    contents: opts.userPrompt,
    config: {
      systemInstruction: opts.systemPrompt,
      maxOutputTokens: opts.maxTokens,
      temperature: opts.temperature,
      ...(opts.responseSchema
        ? {
            responseMimeType: "application/json",
            responseSchema: opts.responseSchema,
          }
        : {}),
    },
  });

  for await (const chunk of response) {
    const text = chunk.text ?? "";
    if (text) {
      yield { text, done: false, provider: "gemini" };
    }
  }
  yield { text: "", done: true, provider: "gemini" };
}

/** Stream from Claude via @anthropic-ai/sdk. */
export async function* streamClaude(
  opts: ProviderStreamOptions,
): AsyncGenerator<StreamChunk, void, unknown> {
  const AnthropicModule = await import("@anthropic-ai/sdk");
  const Anthropic = AnthropicModule.default;
  const client = new Anthropic({
    apiKey: opts.config.apiKey,
    // Required for browser/PWA mode — Anthropic SDK blocks browser requests by default.
    // CORS is still enforced by api.anthropic.com; this only works in Tauri WebView
    // (which bypasses CORS) or with a proxy. In pure browser mode, this will throw
    // a network error that the caller should handle by falling back to another provider.
    dangerouslyAllowBrowser: true,
  });
  const model = opts.modelId ?? defaultModelId("claude");

  // When a JSON schema is provided, use Claude's tool_use pattern for structured output.
  // This constrains generation to valid JSON matching the schema.
  if (opts.responseSchema) {
    const response = await client.messages.create({
      model,
      max_tokens: opts.maxTokens,
      system: opts.systemPrompt,
      messages: [{ role: "user", content: opts.userPrompt }],
      tools: [
        {
          name: "structured_output",
          description: "Return structured data matching the required schema",
          input_schema: opts.responseSchema as { type: "object"; [k: string]: unknown },
        },
      ],
      tool_choice: { type: "tool" as const, name: "structured_output" },
    });

    // Extract the tool_use content block and yield as a single chunk
    for (const block of response.content) {
      if (block.type === "tool_use") {
        yield { text: JSON.stringify(block.input), done: false, provider: "claude" };
        break;
      }
    }
    yield { text: "", done: true, provider: "claude" };
    return;
  }

  const stream = client.messages.stream({
    model,
    max_tokens: opts.maxTokens,
    system: opts.systemPrompt,
    messages: [{ role: "user", content: opts.userPrompt }],
  });

  for await (const event of stream) {
    if (event.type === "content_block_delta" && event.delta.type === "text_delta") {
      yield { text: event.delta.text, done: false, provider: "claude" };
    }
  }
  yield { text: "", done: true, provider: "claude" };
}

/** Stream from OpenAI-compatible APIs (Fireworks, DeepSeek). */
export async function* streamOpenAICompat(
  opts: ProviderStreamOptions,
): AsyncGenerator<StreamChunk, void, unknown> {
  const OpenAIModule = await import("openai");
  const OpenAI = OpenAIModule.default;

  const provider = opts.config.provider;
  const baseURL = opts.config.baseUrl ?? PROVIDER_BASE_URLS[provider] ?? undefined;
  const model = opts.modelId ?? defaultModelId(provider);

  const client = new OpenAI({
    apiKey: opts.config.apiKey,
    baseURL,
    dangerouslyAllowBrowser: true,
  });

  const stream = await client.chat.completions.create({
    model,
    max_tokens: opts.maxTokens,
    temperature: opts.temperature,
    stream: true,
    messages: [
      { role: "system", content: opts.systemPrompt },
      { role: "user", content: opts.userPrompt },
    ],
    ...(opts.responseSchema
      ? provider === "openai" || provider === "together"
        ? {
            response_format: {
              type: "json_schema" as const,
              json_schema: {
                name: "result",
                schema: opts.responseSchema,
                strict: true,
              },
            },
          }
        : {
            response_format: { type: "json_object" as const },
          }
      : {}),
  });

  for await (const chunk of stream) {
    const delta = chunk.choices[0]?.delta?.content;
    if (delta) {
      yield { text: delta, done: false, provider };
    }
  }
  yield { text: "", done: true, provider };
}

/**
 * Dispatch to the correct provider stream based on config.
 * Central routing point used by all agent implementations.
 */
export async function* dispatchStream(
  opts: ProviderStreamOptions,
): AsyncGenerator<StreamChunk, void, unknown> {
  switch (opts.config.provider) {
    case "gemini":
      yield* streamGemini(opts);
      break;
    case "claude":
      yield* streamClaude(opts);
      break;
    case "fireworks":
    case "deepseek":
    case "together":
    case "openai":
      yield* streamOpenAICompat(opts);
      break;
    default:
      throw new Error(`Unknown provider: ${opts.config.provider}`);
  }
}

/** Non-streaming generation with structured output support. */
export async function dispatchGenerate(opts: ProviderStreamOptions): Promise<string> {
  let result = "";
  for await (const chunk of dispatchStream(opts)) {
    result += chunk.text;
  }
  return result;
}
